<template>
    <article>
        <p>
            <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">Node-agent</a>
            is a Prometheus exporter based on eBPF that gathers comprehensive container metrics.
        </p>

        <v-img eager max-width="80%" class="ma-auto mb-6" src="/static/img/blog/ebpf.svg" />

        <h2>Features</h2>

        <h3>TCP connection tracing</h3>

        <p>
            To provide visibility into the relationships between services, the agent traces containers TCP events,
            such as <var>connect()</var> and <var>listen()</var>.
        </p>

        <p>
            Exported metrics are useful for:
        </p>

        <ul>
            <li>
                Obtaining an actual map of inter-service communications.
                It doesn't require integration of distributed tracing frameworks into your code.
            </li>
            <li>
                Detecting connections errors from one service to another.
            </li>
            <li>
                Measuring network latency between containers, nodes and availability zones.
            </li>
        </ul>

        <p>Related blog posts:</p>
        <ul>
            <li>
                <a href="/blog/building-a-service-map-using-ebpf">Building a service map using eBPF</a>
            </li>
            <li>
                <a href="/blog/how-to-ping">How ping measures network round-trip time accurately using SO_TIMESTAMPING</a>
            </li>
            <li>
                <a href="/blog/ebpf-portability">The current state of eBPF portability</a>
            </li>
        </ul>

        <h3>Log patterns extraction</h3>

        <p>
            Log management is usually quite expensive. In most cases, you do not need to analyze each event individually.
            It is enough to extract recurring patterns and the number of the related events.
        </p>

        <p>
            This approach drastically reduces the amount of data required for express log analysis.
        </p>

        <p>
            The agent discovers container logs and parses them right on the node.
        </p>

        <p>At the moment the following sources are supported:</p>
        <ul>
            <li>Direct logging to files in <var>/var/log/</var></li>
            <li>Journald</li>
            <li>Dockerd (JSON file driver)</li>
            <li>Containerd (CRI logs)</li>
        </ul>

        <p>
            To learn more about automated log clustering, check out the blog post
            <a href="/blog/mining-logs-from-unstructured-logs">Mining metrics from unstructured logs</a>.
        </p>

        <h3>Delay accounting</h3>

        <p>
            <a href="https://www.kernel.org/doc/html/latest/accounting/delay-accounting.html" target="_blank">Delay accounting</a>
            allows engineers to accurately identify situations where a container is experiencing a lack of CPU time or waiting for I/O.
        </p>

        <p>
            The agent gathers per-process counters through <a href="https://man7.org/linux/man-pages/man7/netlink.7.html" target="_blank">Netlink</a>
            and aggregates them into per-container metrics:
        </p>

        <ul>
            <li>
                <MetricLink name="container_resources_cpu_delay_seconds_total" exporter="node-agent"/>
            </li>
            <li>
                <MetricLink name="container_resources_disk_delay_seconds_total" exporter="node-agent"/>
            </li>
        </ul>

        <v-img eager max-width="80%" class="ma-auto mb-6" src="/static/img/blog/delay_accounting_aggregation.svg" />

        <p>Related blog posts:</p>
        <ul><li>
            <a href="/blog/linux-delay-accounting">Delay accounting: an underrated feature of the Linux kernel</a>
        </li></ul>

        <h3>Out-of-memory events tracing</h3>

        <p>
            <MetricLink name="container_oom_kills_total" exporter="node-agent"/> metric shows that a container has been terminated by the OOM killer.
        </p>

        <h3>Instance meta information</h3>

        <p>
            If a node is a cloud instance, the agent identifies a cloud provider and collects additional information using the related metadata services.
        </p>

        <p>
            Supported cloud providers:
            <a href="https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/instancedata-data-retrieval.html" target="_blank">AWS</a>,
            <a href="https://cloud.google.com/compute/docs/metadata/overview" target="_blank">GCP</a>,
            <a href="https://docs.microsoft.com/en-us/azure/virtual-machines/linux/instance-metadata-service?tabs=linux" target="_blank">Azure</a>,
            <a href="https://docs.hetzner.cloud/#server-metadata" target="_blank">Hetzner</a>
        </p>

        <p>
            Collected info:
        </p>

        <ul>
            <li>AccountID</li>
            <li>InstanceID</li>
            <li>Instance/machine type</li>
            <li>Region</li>
            <li>AvailabilityZone</li>
            <li>AvailabilityZoneId (AWS only)</li>
            <li>LifeCycle: on-demand/spot (AWS and GCP only)</li>
            <li>Private & Public IP addresses</li>
        </ul>

        <p>Related blog posts:</p>
        <ul>
            <li>
                <a href="/blog/cloud-metadata">Gathering cloud instance metadata in AWS, GCP and Azure</a>
            </li>
        </ul>

        <h2>License</h2>

            <p>
                Coroot-node-agent is licensed under the <a href="https://github.com/coroot/coroot-node-agent/blob/main/LICENSE" target="_blank">Apache License, Version 2.0</a>.
            </p>
            <p>
                The BPF code is licensed under the General Public License, Version 2.0.
            </p>
    </article>
</template>

<script>

import MetricLink from "@/components/MetricLink";

export default {
    components: {MetricLink}

}
</script>

<style scoped>
</style>
